import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Clock, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { inr } from "@/lib/calc";
import { linesOf } from "@/lib/payments";

const dmy = (d) => (d ? d.split("-").reverse().join("/") : "");
const recDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "");
const lateLabel = (n) => (n === 1 ? "Next day" : `${n} days late`);

export const lateEntries = (all, month) =>
  all.flatMap((r) => linesOf(r).filter((l) => l.late_days > 0 && (l.date || "").startsWith(month)).map((l) => ({ r, l })))
    .sort((a, b) => (a.l.date < b.l.date ? 1 : -1));

const exportLate = (list, month) => {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(list.map(({ r, l }) => ({
    "Receipt No": r.receipt_no, Student: r.inputs.childName, Grade: r.inputs.grade, Counsellor: r.inputs.counselorName,
    Payment: l.kind, Amount: Number(l.amount) || 0, Mode: l.mode, Reference: l.reference || "",
    "Paid On": l.date, "Recorded On": recDay(l.recorded_at), "Days Late": l.late_days, "Recorded By": l.recorded_by || "", Reason: l.late_reason || "",
    "Spot Exception": l.late_days >= 2 && r.inputs.spot && l.src.kind !== "part" ? "Yes" : "No",
  }))), "Late entries");
  XLSX.writeFile(wb, `Late_Entries_${month}.xlsx`);
};

export const LateEntries = ({ all }) => {
  const nav = useNavigate();
  const [month, setMonth] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7));
  const list = lateEntries(all, month);
  const nextDay = list.filter((x) => x.l.late_days === 1).length;
  return (
    <div className="rounded-lg border border-sky-200 bg-white p-5" data-testid="late-entries">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display flex items-center gap-2 text-base font-bold text-plum-950">
          <Clock className="h-4 w-4 text-sky-600" />Late-recorded payments
          <span className="rounded-full bg-sky-100 px-2 py-0.5 font-num text-xs text-sky-900" data-testid="late-entries-count">{list.length}</span>
          {list.length > 0 && <span className="text-xs font-normal text-slate-500" data-testid="late-entries-split">{nextDay} next day · {list.length - nextDay} 2+ days</span>}
        </h2>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-40" data-testid="late-entries-month" />
          <Button size="sm" variant="outline" disabled={!list.length} onClick={() => exportLate(list, month)} data-testid="late-entries-export-btn">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel
          </Button>
        </div>
      </div>
      {!list.length ? (
        <p className="text-sm text-slate-500" data-testid="late-entries-empty">No payments were recorded late for this month.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {list.map(({ r, l }, i) => (
            <button key={`${r.id}-${i}`} onClick={() => nav(`/receipts/${r.id}`)} data-testid={`late-entry-${r.receipt_no}-${l.src.kind}-${l.src.index}`}
              className="grid w-full gap-1 py-2.5 text-left text-sm transition-colors duration-150 hover:bg-slate-50 md:grid-cols-[1.4fr_1fr_1fr_2fr] md:items-center md:gap-4">
              <span><span className="font-num mr-2 text-slate-500">{r.receipt_no}</span><span className="font-medium">{r.inputs.childName}</span>
                <span className="ml-2 text-xs text-slate-500">{r.inputs.counselorName}</span></span>
              <span className="text-xs text-slate-600">{l.kind} · ₹ {inr(l.amount)} · {l.mode}</span>
              <span className="text-xs text-slate-600">Paid {dmy(l.date)} · Entered {dmy(recDay(l.recorded_at))}
                <span className={`ml-1.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${l.late_days === 1 ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-900"}`}>{lateLabel(l.late_days)}</span></span>
              <span className="text-xs text-slate-700" data-testid={`late-entry-reason-${r.receipt_no}-${l.src.kind}-${l.src.index}`}>
                <b>Reason:</b> {l.late_reason || "—"}{l.recorded_by ? <span className="text-slate-500"> · by {l.recorded_by}</span> : null}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
